import earthPlace from "../assets/projects/earthPlace/pond.jpg";
import fridge from "../assets/projects/fridge/face-negative.jpg";
import trailblazer from "../assets/projects/trailblazer/trail.jpg";
import babyEscape from "../assets/projects/babyEscape.jpg";
import neuroArcade from "../assets/projects/neuroArcade.jpg";
import oneHandedSwiper from "../assets/projects/oneHandedSwiper.jpg";
import cp from "../assets/projects/cp.jpg";
import solitaire from "../assets/projects/solitaire.jpg";
import planeGame from "../assets/projects/planegame.jpg";
import kronk from "../assets/projects/kronk.jpg";

// hero: the big card at the top · feature: the medium cards · list: "More projects"
export type ProjectTier = "hero" | "feature" | "list";

export interface Project {
    id: string;
    title: string;
    tier: ProjectTier;
    tagline: string;
    // hero and feature cards: a short paragraph under the tagline
    blurb?: string;
    // longer write-up, shown on a project's own page
    description: string[];
    // tech stack and platform only
    stack: string[];
    // omit to show only the status (e.g. earthPlace)
    date?: string;
    status?: string;
    thumbnail?: string;
    // CSS object-position for the card image, when the subject isn't centred
    thumbnailPosition?: string;
    // public source repository; list rows show a GitHub button, otherwise a lock
    source?: string;
    // own page at /projects/<page>; the whole card opens it
    page?: string;
    // the card's button: "try" jumps to the page's Try it out section
    cta?: "try" | "learn";
}

// Hero and feature cards first, then "More projects" in display order: led by
// novelty and breadth beyond XR/HCI (the feature cards cover those), not by date.
export const projects: Project[] = [
    {
        id: "earthPlace",
        title: "earthPlace",
        tier: "hero",
        tagline: "World-locked AR voxel art builder",
        blurb: "A real-time collaborative art tool for decorating public space in AR. Every voxel is anchored to a real-world location and persists for the next person who visits.",
        description: [
            "A cross-platform (iOS, Android, Meta Quest) geo-persistent shared AR voxel sandbox where users build 3D creations anchored to real-world locations, discoverable by anyone who visits.",
            "Evaluated with 30+ participants across controlled and in-the-wild conditions; the studies found the platform stable and usable, shifting the open challenges from technical feasibility to content governance and socio-legal questions.",
            "Continuing development independently toward a follow-up paper and public App Store/Play Store release.",
        ],
        stack: ["Unity", "iOS · Android · Quest 3"],
        status: "in progress",
        thumbnail: earthPlace,
        page: "earthplace",
        cta: "try",
    },
    {
        id: "fridge",
        title: "Talking Fridge",
        tier: "feature",
        tagline: "An AR agent with feelings about your food",
        blurb: "An emotional AI agent acting as the fridge it occupies. It watches what you take out, and it reacts with its own mood.",
        description: [
            "A Quest 3 AR agent combining a real-time conversational LLM, vision-based inventory detection, and emotional expression driven by the Pleasure-Arousal-Dominance model.",
            "Developed a counterbalanced two-axis manipulation (anthropomorphic vs. abstract embodiment; relational directive) to isolate the effects of perceived agency and attachment.",
        ],
        stack: ["Unity", "Quest 3", "OpenAI Realtime"],
        date: "2025—2026",
        thumbnail: fridge,
        thumbnailPosition: "center 78%",
        page: "fridge",
        cta: "learn",
    },
    {
        id: "trailblazer",
        title: "Trailblazer",
        tier: "feature",
        tagline: "Gamified mixed reality cycling",
        blurb: "A Snake and Tron inspired game you play on a bike where a glowing trail follows you and the course fills with items to collect.",
        description: [
            "An exploration of interactions and visuals in mixed reality for the context of cycling.",
            "Designed a mixed reality gamified cycling experience inspired by Snake and Tron for the Meta Quest 3.",
        ],
        stack: ["Unity", "Quest 3"],
        date: "2024—2025",
        thumbnail: trailblazer,
        source: "https://github.com/nour2063/trailblazer",
        page: "trailblazer",
        cta: "learn",
    },
    {
        id: "llmDiscordBot",
        title: "Kronk",
        tier: "list",
        tagline: "A just-for-fun LLM-powered Discord bot with 50+ active users",
        description: [
            "Built for fun with a friend, but engineered seriously: Kronk from The Emperor's New Groove runs on a self-hosted Ollama model, with real tool calling, long-term vector memory, live web search, and music playback in voice channels.",
            "Built single-model tool calling with a config-driven tool registry, and moved the memory system from JSON to SQLite with sqlite-vec for conversation recall.",
            "Wrote a detector and eval suite that catches replies claiming actions no tool actually performed, used to compare models, thinking modes, and prompts against real production failures.",
            "Made it run reliably as a background service, with a single-instance guard against duplicate replies and a self-recovering watchdog.",
        ],
        stack: ["Python", "Ollama", "SQLite"],
        date: "2026",
        thumbnail: kronk,
        source: "https://github.com/AndreiGhita2002/llm-discord-bot",
    },
    {
        id: "planeGame",
        title: "PlaneGame",
        tier: "list",
        tagline: "Hackathon-winning error page game",
        description: [
            "Small game for error pages on a booking website built in under 26 hours.",
            "Guide planes to their respective destination.",
            "Winner of freetobook's challenge from the Do You Have the GUTS? 2025 hackathon.",
        ],
        stack: ["Web", "PixiJS"],
        date: "2025",
        thumbnail: planeGame,
        source: "https://github.com/AndreiGhita2002/plane-game/",
    },
    {
        id: "neuroArcade",
        title: "NeuroArcade",
        tier: "list",
        tagline: "Research results platform",
        description: [
            "A unified web app implemented with Django + React to display, process, and upload results from neuroscience research 'brain games' played by humans and AI models hosted on third party platforms.",
            "Took on lead demonstrator role in an Agile Scrum team.",
        ],
        stack: ["Django", "React"],
        date: "2023—2024",
        thumbnail: neuroArcade,
        source: "https://github.com/nathanlogie/neuro_arcade",
    },
    {
        id: "footballMates",
        title: "Football Mates",
        tier: "list",
        tagline: "Voice-driven duelling football pundits",
        description: [
            "Conversational multi-agent system with deterministic tool calling, grounding live football stats in real API responses rather than model memory.",
            "The system uses two embodied, opposing pundit personas (a stats-driven optimist and an old-school sceptic) to turn straightforward football queries into natural, debate-driven dialogue.",
            "Fully integrated voice pipeline using Whisper for speech recognition and Edge TTS for synthesis, letting users phone in as a guest caller and hear both hosts respond in character.",
        ],
        stack: ["Whisper", "Edge TTS"],
        date: "2026",
        status: "coursework",
    },
    {
        id: "babyEscape",
        title: "Baby Escape",
        tier: "list",
        tagline: "VR escape room as a crawling baby",
        description: [
            "An escape room in virtual reality where the player embodies a baby. Designed with the 'grab move' locomotion technique to imitate crawling, and the use of microphone input for puzzle interaction.",
            "Developed in a team of 4, with a focus on providing unique XR interactions and mitigating cyber sickness.",
        ],
        stack: ["Unity", "VR"],
        date: "2024",
        status: "coursework",
        thumbnail: babyEscape,
        source: "https://github.com/babyEscape/babyEscape",
    },
    {
        id: "bb84",
        title: "BB84 Quantum Key Exchange",
        tier: "list",
        tagline: "Simulated protocol with an attacker",
        description: [
            "A simulated BB84 quantum key exchange protocol built with Qiskit.",
            "A version with an attacker was made to demonstrate how those in communication are informed.",
        ],
        stack: ["Qiskit"],
        date: "2025",
        status: "coursework",
    },
    {
        id: "cp",
        title: "Firefighter CP Solver",
        tier: "list",
        tagline: "Constraint programming model",
        description: [
            "The firefighter problem models a discrete-time diffusion process on a graph, where a fire initially ignites at a specific set of vertices, then spreads out.",
            "This problem is a modified version which includes an infectious defence. The aim is to minimize burnt nodes, or maximise safe nodes.",
            "The CP model's success is attributed to the use of an auxiliary variable for each node indicating whether or not it is scheduled to change in any given turn.",
            "It solves a 6x6 grid with an initial fire in (2, 3) in just 143ms on an M3 Macbook Pro, far exceeding the expectations of the course coordinator.",
        ],
        stack: ["MiniZinc"],
        date: "2024",
        status: "coursework",
        thumbnail: cp,
    },
    {
        id: "minecraft",
        title: "Minecraft Server on Demand",
        tier: "list",
        tagline: "Dockerized server, Discord remote control",
        description: [
            "An endeavour I took on after learning cloud system infrastructure to deploy a dockerized Minecraft server on the Google Cloud Compute Engine.",
            "A separate free-tier virtual machine ran a python script which handled a Discord bot serving as a chat interface for remotely controlling the application, as well as frequent player count polls.",
            "If there were no players after a specified period of time, the server VM would suspend. Players would type in '/start' on Discord alerting the bot to push a request to resume the VM.",
        ],
        stack: ["Docker", "Google Cloud", "Discord API"],
        date: "2025",
        status: "private",
    },
    {
        id: "solitaire",
        title: "CLI Solitaire",
        tier: "list",
        tagline: "Solitaire in the terminal",
        description: [
            "The game Solitaire in a command line interface built with functional programming principles in Haskell.",
        ],
        stack: ["Haskell"],
        date: "2024",
        status: "coursework",
        thumbnail: solitaire,
    },
    {
        id: "spre",
        title: "Extreme Startup Client",
        tier: "list",
        tagline: "Live API service with CI/CD",
        description: [
            "Multi-module Spring Boot ExtremeStartup client service; maintains a live HTTP endpoint responding to a constant stream of API queries spanning 40+ question types — arithmetic, sequences, primality, and trivia.",
            "Weighted-random delegation routing proxies questions to other registered instances, with loop detection (HTTP 508) and a circuit breaker that trips after consecutive delegation failures.",
            "Spring Boot Actuator health indicators exposing live metrics, heartbeat-based re-registration, and a GitLab CI/CD pipeline with nightly builds and JaCoCo coverage enforcement.",
        ],
        stack: ["Spring Boot"],
        date: "2026",
        status: "coursework",
    },
    {
        id: "oneHandedSwiper",
        title: "One-Handed Swiper",
        tier: "list",
        tagline: "Remote swipe interaction prototype",
        description: [
            "A proof-of-concept React-based web app for a remote swipe interaction prototype shown with playing cards.",
        ],
        stack: ["React"],
        date: "2024",
        status: "coursework",
        thumbnail: oneHandedSwiper,
        source: "https://github.com/nour2063/one-handed-swiper",
    },
    {
        id: "site",
        title: "This website",
        tier: "list",
        tagline: "Designed and built from scratch",
        description: [
            "A personal site built from scratch in React and TypeScript, which doubles as my scratchpad for trying out web design ideas and learning new things.",
            "Includes an interactive map in the photos page, built on MapLibre, with hiking and cycling routes taken from Strava and Komoot GPX activity recordings and OpenStreetMap.",
        ],
        stack: ["React", "TypeScript", "MapLibre"],
        date: "2024—2026",
        status: "private",
    },
];

// Sub-pages under /projects/: each project's own page, plus earthPlace's
// privacy policy and support pages.
export const projectSubpages = [
    ...projects.filter(p => p.page).map(p => p.page as string),
    "earthplace/privacy",
    "earthplace/support",
];
